/**
 * TikTok Platform Media Restrictions
 * Based on official TikTok API specifications
 *
 * @see https://developers.tiktok.com/doc/content-posting-api-get-started
 * @see https://developers.tiktok.com/doc/content-posting-api-reference-video-upload
 */

export const TIKTOK_RESTRICTIONS = {
  video: {
    // Sandbox mode (pre-verification)
    sandbox: {
      maxFileSize: 4 * 1024 * 1024 * 1024, // 4 GB
      maxUploadsPerDay: 10, // Limited in sandbox mode
    },
    // Production mode (after verification)
    production: {
      maxFileSize: 4 * 1024 * 1024 * 1024, // 4 GB
      maxUploadsPerDay: 1000, // Significantly higher in production
    },
    allowedFormats: [
      'video/mp4',
      'video/quicktime', // .mov
      'video/webm',
    ],
    minDuration: 3, // 3 seconds minimum
    maxDuration: 10 * 60, // 10 minutes
    recommendedDuration: {
      short: 15, // 15 seconds (original TikTok format)
      long: 3 * 60, // 3 minutes (for longer content)
    },
    aspectRatio: {
      vertical: 9 / 16, // 9:16 (most common, recommended)
      square: 1 / 1, // 1:1
      horizontal: 16 / 9, // 16:9
    },
    recommendedResolution: {
      vertical: { width: 1080, height: 1920 }, // 1080x1920
      square: { width: 1080, height: 1080 }, // 1080x1080
      horizontal: { width: 1920, height: 1080 }, // 1920x1080
    },
    minResolution: { width: 360, height: 640 }, // Minimum supported
    maxResolution: { width: 4096, height: 2304 }, // Maximum supported
    frameRate: {
      min: 23,
      max: 60,
      recommended: 30, // 30 fps is standard
    },
  },
  caption: {
    maxLength: 2200, // characters (including hashtags and mentions)
    // Note: Captions are combined with hashtags in TikTok
  },
  privacy: {
    // Privacy levels available
    levels: {
      public: 'PUBLIC_TO_EVERYONE', // Visible to everyone
      friends: 'MUTUAL_FOLLOW_FRIENDS', // Only visible to mutual followers
      private: 'SELF_ONLY', // Only visible to the user (sandbox default)
    },
    // Pre-verification restrictions
    sandbox: {
      default: 'SELF_ONLY', // Sandbox mode forces SELF_ONLY
      availableLevels: ['SELF_ONLY'], // Only SELF_ONLY available before verification
    },
    production: {
      default: 'PUBLIC_TO_EVERYONE', // Production can use any level
      availableLevels: ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY'],
    },
  },
  engagement: {
    // Engagement settings (comments, duet, stitch)
    allowComments: true, // Can disable comments
    allowDuet: true, // Can disable duets
    allowStitch: true, // Can disable stitches
  },
  photo: {
    // Photo carousels via /v2/post/publish/content/init/ — separate endpoint
    // from the video flow. Title is capped at 90 chars (vs 2200 for caption).
    maxItems: 35,
    minItems: 1,
    maxTitleLength: 90,
    maxDescriptionLength: 4000,
    allowedFormats: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  },
  general: {
    // TikTok supports either a single video OR a photo carousel — but not
    // mixed image+video in the same post (different API endpoints).
    supportsImages: true,
    supportsDocuments: false,
    requiresVideo: false,
  },
  rateLimit: {
    // API rate limits (per user per day)
    sandbox: {
      uploads: 10, // Max 10 uploads per day in sandbox
    },
    production: {
      uploads: 1000, // Max 1000 uploads per day in production
    },
  },
} as const

export type TikTokRestrictions = typeof TIKTOK_RESTRICTIONS
